<template>
  <div class="delivery-issues-wrapper">
    <!-- Header with Create Issue Button -->
    <div class="issues-header">
      <h3 class="issues-title"></h3>
      <PermissionGuard permission="CREATE_ISSUE">
        <AppButton @click="openCreateIssueModal" variant="primary" class="create-issue-btn">
          <svg viewBox="0 0 24 24" width="20" height="20" class="btn-icon">
            <path fill="currentColor" d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
          </svg>
          {{ $t('issues.addIssue') }}
        </AppButton>
      </PermissionGuard>
    </div>

    <!-- Modern Issues Grid -->
    <div class="issues-grid-container">
      <div v-if="loading" class="loading-state">
        <div class="loading-spinner"></div>
        <h4>{{ $t('common.loading') }}</h4>
        <p>{{ $t('issues.fetchingIssues') }}</p>
      </div>
      
      <div v-else-if="error" class="error-state">
        <div class="error-icon">
          <svg viewBox="0 0 24 24" width="48" height="48">
            <path fill="currentColor" d="M12,2L13.09,8.26L22,9L13.09,9.74L12,16L10.91,9.74L2,9L10.91,8.26L12,2M12,21.5A9.5,9.5 0 0,1 2.5,12A9.5,9.5 0 0,1 12,2.5A9.5,9.5 0 0,1 21.5,12A9.5,9.5 0 0,1 12,21.5M12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20Z" />
          </svg>
        </div>
        <h4>{{ $t('common.error') }}</h4>
        <p>{{ error }}</p>
        <AppButton @click="fetchIssues" variant="outline" class="retry-btn">
          <svg viewBox="0 0 24 24" width="16" height="16">
            <path fill="currentColor" d="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z" />
          </svg>
          {{ $t('common.retry') }}
        </AppButton>
      </div>
      
      <div v-else-if="issues.length === 0" class="empty-state">
        <div class="empty-icon">
          <svg viewBox="0 0 24 24" width="64" height="64">
            <path fill="currentColor" d="M13,9H18.5L13,3.5V9M6,2H14L20,8V20A2,2 0 0,1 18,22H6C4.89,22 4,21.1 4,20V4C4,2.89 4.89,2 6,2M15,18V16H6V18H15M18,14V12H6V14H18Z" />
          </svg>
        </div>
        <h3>{{ $t('issues.noIssuesFound') }}</h3>
        <p>{{ $t('issues.getStarted') }}</p>
        <PermissionGuard permission="CREATE_ISSUE">
          <AppButton @click="openCreateIssueModal" variant="primary" class="create-first-issue">
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path fill="currentColor" d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
            </svg>
            {{ $t('issues.addIssue') }}
          </AppButton>
        </PermissionGuard>
      </div>

      <div v-else class="grid-content">
        <AppDataGrid
          :columnDefs="columnDefs"
          :rowData="issues"
          :defaultColDef="defaultColDef"
          :pagination="true"
          :pageSize="15"
          :pageSizeOptions="[15, 50, 100]"
          :suppressPaginationPanel="false"
          :animateRows="true"
          :showSearch="true"
          :searchPlaceholder="$t('issues.placeholders.searchIssues')"
          @grid-ready="onGridReady"
          @action-clicked="handleAction"
          :suppressMovableColumns="false"
          gridStyle="width: 100%;"
          domLayout="autoHeight"
          :actions="actions"
        />
      </div>
    </div>

    <!-- Issue Form Modal -->
    <IssueFormModal
      v-if="showIssueModal"
      :visible="showIssueModal"
      :issue="selectedIssue"
      :project-id="projectId"
      :intervenants="intervenants"
      @close="closeIssueModal"
      @issue-saved="onIssueSaved"
    />

    <!-- Delete Confirmation Modal -->
    <ConfirmModal
      v-if="showDeleteConfirm"
      :isVisible="showDeleteConfirm"
      :title="$t('issues.deleteIssue')"
      :details="deleteMessage"
      :confirm-text="$t('common.delete')"
      :cancel-text="$t('common.cancel')"
      type="danger"
      @confirm="confirmDelete"
      @cancel="cancelDelete"
    />
  </div>
</template>

<script setup lang="ts">
/// <reference types="vue" />
// @ts-ignore
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useNotifications } from '@/composables/useNotifications';
import { usePermissions } from '@/composables/usePermissions';
import { useAuthStore } from '@/stores';

// Components
import AppButton from '../../base/AppButton.vue';
import AppDataGrid from '../../base/AppDataGrid.vue';
import IssueFormModal from './IssueFormModal.vue';
import ConfirmModal from '../../common/ConfirmModal.vue';
import PermissionGuard from '../../common/PermissionGuard.vue';

// Services
import deliveryIssuesService from '@/services/deliveryIssues.service';
import intervenantsService from '@/services/intervenants.service';
import { PermissionType } from '@/services/permission.service';
import type { Issue } from '@/types/issue';

interface Intervenant {
  id: number;
  name: string;
}

interface Props {
  projectId: string | number;
}

interface Emits {
  'issue-created': [issue: Issue];
  'issue-updated': [issue: Issue];
  'issue-deleted': [issue: Issue];
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const { t } = useI18n();
const { notifySuccess, notifyError } = useNotifications();
const { hasPermissionForProject } = usePermissions();
const router = useRouter();

// Reactive data
const issues = ref<Issue[]>([]);
const intervenants = ref<Intervenant[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);
const gridApi = ref<any>(null);

// Modal states
const showIssueModal = ref(false);
const selectedIssue = ref<Issue | null>(null);
const showDeleteConfirm = ref(false);
const issueToDelete = ref<Issue | null>(null);

// Computed properties
const deleteMessage = computed(() => {
  if (!issueToDelete.value) return '';
  return t('issues.confirmation.deleteIssue');
});

// Grid configuration
const defaultColDef = ref({
  resizable: true,
  sortable: true,
  filter: false,
  floatingFilter: false
});

// Column definitions - make reactive to language changes
const columnDefs = computed(() => [
  {
    field: 'title',
    headerName: t('issues.title'),
    type: 'text',
    flex: 2,
    minWidth: 200,
    sortable: true,
    resizable: true
  },
  {
    field: 'status',
    headerName: t('issues.status'),
    width: 140,
    sortable: true,
    resizable: true,
    cellRenderer: (params: any) => {
      const status = params.value;
      const statusClass = `status-badge status-${status?.toLowerCase()}`;
      return `<span class="${statusClass}">${t(`issues.statuses.${status}`)}</span>`;
    }
  },
  {
    field: 'priority',
    headerName: t('issues.priority'),
    width: 120,
    sortable: true,
    resizable: true,
    cellRenderer: (params: any) => {
      const priority = params.value;
      const priorityClass = `priority-badge priority-${priority?.toLowerCase()}`;
      return `<span class="${priorityClass}">${t(`issues.priorities.${priority}`)}</span>`;
    }
  },
  {
    field: 'reporterName',
    headerName: t('issues.reportedBy'),
    type: 'text',
    width: 150,
    sortable: true,
    resizable: true
  },
  {
    field: 'assigneeName',
    headerName: t('issues.assignedTo'),
    type: 'text',
    width: 150,
    sortable: true,
    resizable: true
  },
  {
    field: 'createdAt',
    headerName: t('issues.reportedDate'),
    type: 'date',
    width: 140,
    sortable: true,
    resizable: true
  },
  {
    field: 'resolvedDate',
    headerName: t('issues.resolvedDate'),
    type: 'date',
    width: 140,
    sortable: true,
    resizable: true
  }
]);

// Get current user ID
const authStore = useAuthStore();

// Define actions - make reactive to language changes
const actions = computed(() => {
  const currentUserId = authStore.getUser?.id;
  const availableActions = [];
  
  // Always add view details action
  availableActions.push({
    name: 'viewDetails',
    label: t('issues.details.viewDetails'),
    icon: 'fas fa-eye',
    variant: 'primary'
  });
  
  // Edit action - only show if current user is the creator
  if (hasPermissionForProject(PermissionType.UPDATE_ISSUE, props.projectId).value) {
    availableActions.push({
      name: 'edit',
      label: t('common.edit'),
      icon: 'fas fa-edit',
      variant: 'secondary',
      condition: (rowData: Issue) => rowData.createdBy === currentUserId
    });
  }
  
  // Delete action - only show if current user is the creator
  if (hasPermissionForProject(PermissionType.DELETE_ISSUE, props.projectId).value) {
    availableActions.push({
      name: 'delete',
      label: t('common.delete'),
      icon: 'fas fa-trash',
      variant: 'danger',
      condition: (rowData: Issue) => rowData.createdBy === currentUserId
    });
  }
  
  return availableActions;
});

// Methods
const fetchIssues = async () => {
  try {
    loading.value = true;
    error.value = null;

    const response = await deliveryIssuesService.getIssues(props.projectId.toString());
    issues.value = response || [];
    
  } catch (err: any) {
    console.error('Error fetching issues:', err);
    error.value = err.message || t('issues.errors.failedToLoad');
  } finally {
    loading.value = false;
  }
};

const fetchIntervenants = async () => {
  try {
    const response = await intervenantsService.getIntervenants(props.projectId.toString());
    if (response && Array.isArray(response)) {
      intervenants.value = response.map((i: any) => ({
        id: i.id,
        name: i.name
      }));
    }
  } catch (err: any) {
    console.error('Error fetching intervenants:', err);
  }
};

const openCreateIssueModal = () => {
  selectedIssue.value = null;
  showIssueModal.value = true;
};

const closeIssueModal = () => {
  showIssueModal.value = false;
  selectedIssue.value = null;
};

const viewIssue = (issue: Issue) => {
  selectedIssue.value = { ...issue };
  showIssueModal.value = true;
};

const deleteIssue = (issue: Issue) => {
  issueToDelete.value = issue;
  showDeleteConfirm.value = true;
};

// Grid event handlers
const handleAction = (action: any, data: any) => {
  switch (action.name) {
    case 'viewDetails':
      router.push(`/project/${props.projectId}/issues/${data.id}`);
      break;
    case 'edit':
      viewIssue(data);
      break;
    case 'delete':
      deleteIssue(data);
      break;
    default:
      console.warn('Unknown action:', action.name);
  }
};

const onGridReady = (api: any) => {
  console.log('AppDataGrid is ready', api);
  gridApi.value = api;
};

const onIssueSaved = async (issue: Issue, isNew: boolean) => {
  const message = isNew ? 
    t('issues.messages.created') : 
    t('issues.messages.updated');
  notifySuccess(message);
  
  emit(isNew ? 'issue-created' : 'issue-updated', issue);
  await fetchIssues();
};

const confirmDelete = async () => {
  if (!issueToDelete.value) return;
  
  try {
    await deliveryIssuesService.deleteIssue(
      props.projectId.toString(),
      issueToDelete.value.id.toString()
    );
    
    notifySuccess(t('issues.messages.deleted'));
    
    emit('issue-deleted', issueToDelete.value);
    await fetchIssues();
    
  } catch (err: any) {
    console.error('Error deleting issue:', err);
    notifyError(err.message || t('issues.errors.failedToDelete'));
  } finally {
    showDeleteConfirm.value = false;
    issueToDelete.value = null;
  }
};

const cancelDelete = () => {
  showDeleteConfirm.value = false;
  issueToDelete.value = null;
};

// Lifecycle
onMounted(() => {
  fetchIssues();
  fetchIntervenants();
});
</script>

<style scoped>
.delivery-issues-wrapper {
  max-height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  overflow: hidden;
}

.create-issue-btn,
.create-first-issue {
  display: flex;
  align-items: center;
  gap: 8px;
}

.issues-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
}

.issues-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  color: var(--neutral-900);
}

.issues-grid-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  height: 100%;
}

.loading-state,
.error-state,
.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e5e7eb;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-icon,
.empty-icon {
  margin-bottom: 16px;
  color: #9ca3af;
}

.error-icon svg {
  color: #ef4444;
}

.error-state h4,
.empty-state h3 {
  margin: 0 0 8px 0;
  color: var(--neutral-900);
}

.error-state p,
.empty-state p {
  margin: 0 0 16px 0;
  color: #6b7280;
}

.grid-content {
  flex: 1;
  overflow: hidden;
}

/* Status badges */
:deep(.status-badge) {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 0.8125rem;
  font-weight: 600;
  text-transform: capitalize;
  white-space: nowrap;
  letter-spacing: 0.01em;
  border: 1px solid transparent;
  transition: all 0.2s ease;
}

:deep(.status-open) {
  background-color: #dbeafe;
  color: #1e40af;
  border-color: #bfdbfe;
}

:deep(.status-in_progress) {
  background-color: #fef3c7;
  color: #92400e;
  border-color: #fde68a;
}

:deep(.status-paused) {
  background-color: #fed7aa;
  color: #9a3412;
  border-color: #fdba74;
}

:deep(.status-resolved) {
  background-color: #d1fae5;
  color: #065f46;
  border-color: #a7f3d0;
}

:deep(.status-closed) {
  background-color: #f3f4f6;
  color: #374151;
  border-color: #e5e7eb;
}

:deep(.status-rejected) {
  background-color: #fee2e2;
  color: #991b1b;
  border-color: #fecaca;
}

/* Priority badges */
:deep(.priority-badge) {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 0.8125rem;
  font-weight: 600;
  text-transform: capitalize;
  white-space: nowrap;
  letter-spacing: 0.01em;
  border: 1px solid transparent;
  transition: all 0.2s ease;
}

:deep(.priority-low) {
  background-color: #f3f4f6;
  color: #4b5563;
  border-color: #e5e7eb;
}

:deep(.priority-medium) {
  background-color: #dbeafe;
  color: #1e40af;
  border-color: #bfdbfe;
}

:deep(.priority-high) {
  background-color: #fed7aa;
  color: #9a3412;
  border-color: #fdba74;
}

:deep(.priority-critical) {
  background-color: #fee2e2;
  color: #991b1b;
  border-color: #fecaca;
  font-weight: 700;
}
</style>
